import { useCallback, useMemo, useState } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import type { LegacyReportCropInput } from "../types/legacyReportReview";
import {
  sourceCropRegion,
  sourcePagePoint,
  type LegacyCropPageSize,
  type LegacyCropPoint,
} from "../utils/legacyReportCrop";

interface Props {
  page: LegacyCropPageSize;
  region: LegacyReportCropInput["region"];
  enabled: boolean;
  onChange: (region: LegacyReportCropInput["region"]) => void;
  onPendingChange: (pending: boolean) => void;
}

export function useLegacyReportCropSelection({
  page,
  region,
  enabled,
  onChange,
  onPendingChange,
}: Props) {
  const [first, setFirst] = useState<LegacyCropPoint | null>(null);
  const [cursor, setCursor] = useState<LegacyCropPoint>({
    x: page.width / 2,
    y: page.height / 2,
  });
  const [keyboardCursor, setKeyboardCursor] = useState(false);
  const [selectionError, setSelectionError] = useState(false);
  const visibleRegion = useMemo(
    () => (first ? sourceCropRegion(first, cursor, page) : region),
    [first, cursor, page, region]
  );
  const selectCorner = useCallback(
    (point: LegacyCropPoint) => {
      setCursor(point);
      setSelectionError(false);
      if (!first) {
        setFirst(point);
        onPendingChange(true);
        return;
      }
      const selected = sourceCropRegion(first, point, page);
      if (selected.width < 1 || selected.height < 1) {
        setSelectionError(true);
        return;
      }
      onChange(selected);
      setFirst(null);
      onPendingChange(false);
    },
    [first, onChange, onPendingChange, page]
  );
  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!enabled) return;
      const point =
        event.detail === 0
          ? cursor
          : sourcePagePoint(
              { x: event.clientX, y: event.clientY },
              event.currentTarget.getBoundingClientRect(),
              page
            );
      if (!point) return;
      if (event.detail !== 0) setKeyboardCursor(false);
      selectCorner(point);
    },
    [enabled, cursor, page, selectCorner]
  );
  const handleMouseMove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!enabled || !first) return;
      const point = sourcePagePoint(
        { x: event.clientX, y: event.clientY },
        event.currentTarget.getBoundingClientRect(),
        page
      );
      if (point) setCursor(point);
    },
    [enabled, first, page]
  );
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (!enabled || !event.key.startsWith("Arrow")) return;
      event.preventDefault();
      setKeyboardCursor(true);
      const step = event.shiftKey ? 0.001 : 0.01;
      const dx =
        event.key === "ArrowLeft"
          ? -step
          : event.key === "ArrowRight"
          ? step
          : 0;
      const dy =
        event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
      setCursor((previous) => ({
        x: Math.max(0, Math.min(page.width, previous.x + dx * page.width)),
        y: Math.max(0, Math.min(page.height, previous.y + dy * page.height)),
      }));
    },
    [enabled, page]
  );
  const handleFocus = useCallback(() => setKeyboardCursor(true), []);
  const handleCancel = useCallback(() => {
    setFirst(null);
    setSelectionError(false);
    onPendingChange(false);
  }, [onPendingChange]);
  const handleFullPage = useCallback(() => {
    handleCancel();
    onChange({ x: 0, y: 0, width: page.width, height: page.height });
  }, [handleCancel, onChange, page]);
  return {
    first,
    cursor,
    keyboardCursor,
    visibleRegion,
    selectionError,
    handleClick,
    handleMouseMove,
    handleKeyDown,
    handleFocus,
    handleCancel,
    handleFullPage,
  };
}
